import { CustomerInfo } from '@/domain/CustomerInfo'
import { AgencyInfo } from '@/domain/AgencyInfo'
import { InvoiceToInfo } from '@/domain/InvoiceToInfo'
import { UserInfo } from '@/domain/UserInfo'
import { InspectionInfo } from '@/domain/InspectionInfo'
import { PriceInfo } from '@/domain/PriceInfo'
import { SumInfo } from '@/domain/SumInfo'
import { ImportInfo } from '@/domain/ImportInfo'
import { PaymentInfo } from '@/domain/PaymentInfo'
import FileData from '@/domain/FileData'
import DateUtils from '@/utils/DateUtils'

/**
 * 請求明細
 */
export class InvoiceDetail {
    /** 受付番号 */
    requestNo = ""
    /** 検査番号 */
    inspectNo = ""
    /** 検体名称 */
    specimenName = ""
    /** 検査情報 */
    inspectionInfo = new InspectionInfo()
    /* 価格情報 */
    priceInfo = new PriceInfo()

    /** 報告書作成日 YYYY-MM-DD */
    reportOn = ""

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<InvoiceDetail>) {
        Object.assign(this, init)
    }
}

/**
 * 請求
 */
export class Invoice {
    /** 請求番号 */
    invoiceNo = ""
    /** 請求日 YYYY-MM-DD */
    invoiceOn = ""
    /** 件名 */
    subject = ""
    /** 支払期限 */
    maturity = ""
    /** 検査依頼種別 */
    requestType = 0
    /** 作成者 */
    authorUser = new UserInfo()
    /** 承認者 */
    authorizerUser = new UserInfo()
    /** 顧客情報 */
    customerInfo = new CustomerInfo()
    /** 代理店 */
    agencyInfo = new AgencyInfo()
    /** 請求先 */
    invoiceToInfo = new InvoiceToInfo()
    /**請求番号枝番**/
    invoiceBrNo = 0
    /** 検体名称(複数) */
    specimenNames = ""
    /** 検体識別記号(複数) */
    specimenIds = ""
    /** 集計情報 */
    sumInfo = new SumInfo()
    /** 輸入情報 */
    importInfo = new ImportInfo()
    /** 備考 */
    note = ""
    /** 入金情報 */
    paymentInfo = new PaymentInfo()
    /** メール送信日時 */
    mailSendAt: string | null = null
    /** 印刷日時 */
    printAt: string | null = null
    /** 角印の押印 0:なし 1:あり */
    imprint = true
    /** 請求日印刷有無 0:なし 1:あり */
    printInvoiceOn = true

    /** 代理店請求番号 */
    agencyInvoiceNo = ""
    /** 合算請求番号 */
    totalInvoiceNo = ""

    /** 請求明細 */
    details = [new InvoiceDetail()]

    /** 請求書 */
    invoiceFile: FileData = new FileData()

    /** 受付番号 */
    requestNos: string[] = []

    /** 前の請求番号 */
    previousInvoiceNo = ""

    /** 次の請求番号 */
    nextInvoiceNo = ""

    /** 報告書数 */
    reportCount = 0

    /** 送信済み報告書数 */
    reportSendCount = 0

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<Invoice>) {
        Object.assign(this, init)
    }

    /**
     * 複製を作成する
     */
    clone() {
        return new Invoice(JSON.parse(JSON.stringify(this)));
    }

    /**
     * 宛先の会社が空か
     */
    get isEmptyCustomer() {
        return this.customerInfo.customerPrefectureCode === "" || this.customerInfo.customerCode === "" || this.customerInfo.customerBranchNumber === ""
    }

    /**
     * 代理店経由か
     */
    get hasAgency() {
        return this.agencyInfo.agencyPrefectureCode !== null && this.agencyInfo.agencyCode !== null || this.agencyInfo.agencyBranchNumber !== null
    }

    /**
     * メール送信済みか？
     */
    get isMailSended() {
        return !(this.mailSendAt === null || this.mailSendAt === "")
    }

    /**
     * 印刷済みか？
     */
    get isPrinted() {
        return !(this.printAt === null || this.printAt === "")
    }

    /**
     * 入金済みか？
     * true: 入金済  false: 未入金  null: 入金金額過不足あり
     */
    get paymentStatus(): boolean | null {
        const depositAmount = this.sumInfo.amount + this.sumInfo.tax
        if (this.paymentInfo.depositAmount == 0 && depositAmount != 0) {
            return false
        } else if (this.paymentInfo.depositAmount == depositAmount || depositAmount == 0){
            return true
        } else {
            return null
        }
    }

    /**
     * ロック状態(編集不可)か？
     */
    get isLocked() {
        return this.agencyInvoiceNo !== "" || this.totalInvoiceNo !== ""
    }

    /**
     * 小計
     */
    get amount() {
        return this.details
            .map(x => x.priceInfo.unitPrice * x.priceInfo.quantity)
            .reduce((p, x) => p + x, 0)
    }

    /**
     * 小計（カンマ区切り）
     */
    get amountText() {
        return isNaN(this.amount) ? "-" : this.amount.toLocaleString()
    }

    /**
     * 消費税率
     */
    get taxRate() {
        return this.details[0].priceInfo.taxRate
    }



    /**
     * 消費税
     */
    get tax() {
				/*
        return this.details
            .map(x => Math.floor(x.priceInfo.unitPrice * x.priceInfo.quantity * (x.priceInfo.taxRate / 100)))
            .reduce((p, x) => p + x, 0)
				*/
        return Math.floor(this.amount * (this.taxRate / 100))
    }

    /**
     * 消費税（カンマ区切り）
     */
    get taxText() {
        return isNaN(this.tax) ? "-" : this.tax.toLocaleString()
    }

    /**
     * 合計金額
     */
    get totalAmount() {
        return this.amount + this.tax
    }

    /**
     * 合計金額（カンマ区切り）
     */
    get totalAmountText() {
        return isNaN(this.totalAmount) ? "-" : this.totalAmount.toLocaleString()
    }

    /**
     * フィルターキーワードの取得
     */
    get filterKeyword() {
        const dateUtils = DateUtils.instance
        return `${dateUtils.toJapanFormat(this.invoiceOn)} ${this.invoiceNo} ${this.customerInfo.customerName} ${this.agencyInfo.agencyName} ${this.specimenNames} ${this.specimenIds} ${this.sumInfo.inspectionNames} ${this.sumInfo.classificationNames}`
    }
    
    /**
     * 明細行を追加
     * @param detail 
     */
    addDetailLine(detail = new InvoiceDetail()) {
        this.details.push(detail)
    }

    /**
     * 明細行を削除
     * @param lineIndex 
     */
    deleteDeailLine(lineIndex: number) {
        this.details.splice(lineIndex, 1)
    }

}

/**
 * メール送信キュー
 */
export class MailQueue {
    /** TO宛先 */
    to = ""
    /** CC宛先 */
    cc: string | null = null
    /** BCC宛先 */
    bcc: string | null = null
    /** REPLY TO  */
    replyTo: string | null = null
    /** 件名 */
    subject = ""
    /** 本文 */
    content = ""
    /** 請求番号 */
    invoiceNos: string[] = []
    /** 報告書送付モード 0:送付しない 1:請求書に添付 2:報告書のみ */
    reportFileSendMode = 0
    /** メール送信日時 */
    mailSendAt: string | null = null

    /** 添付報告書番号 */
    reportNos: string[] = []

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<MailQueue>) {
        Object.assign(this, init)
    }
}

/**
 * 入金
 */
export class DepositAmount {
    /** 入金日 */
    paymentOn: string | null = null
    /** 請求番号 */
    invoiceNo = ""
    /** 入金金額 */
    depositAmount = 0

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<DepositAmount>) {
        Object.assign(this, init)
    }
    
    /**
     * 複製を作成する
     */
    clone() {
        return new DepositAmount(JSON.parse(JSON.stringify(this)));
    }
}
